import { Check } from '@mui/icons-material';
import { Paper, Stack, Typography, styled } from '@mui/material';
import {
  ConsultTab,
  EconsultUserRoles,
  NoteActionTypes,
  NoteTypes
} from '@oh-vcp/components-common';
import { CaseNote } from '@oh-vcp/components-common/src/models/case/CaseDetails';
import moment from 'moment';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import EconsultAttachmentLink from '../econsultAttachmentLink/EconsultAttachmentLink';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import EconsultUserService from '../services/econsultUserService';
import { useEconsultTabContext } from '../econsultTabContext/EconsultTabContext';

interface EconsultCaseNoteItemProps {
  note: CaseNote;
}

const EconsultCaseNoteItemPaper = styled(Paper)({
  paddingLeft: 15,
  paddingRight: 15,
  paddingTop: 30,
  paddingBottom: 30,
  marginBottom: 20,
  borderRadius: 0,
  boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 5px 0px'
});

const CheckMarkIcon = styled(Check)(({ theme }) => ({
  color: theme.palette.primary.custom3,
  fontSize: 18
}));

const EconsultCaseNoteItem: FC<EconsultCaseNoteItemProps> = ({ note }) => {
  const {
    actionTypeCd,
    lastUpdated,
    noteContent,
    noteTypeCd,
    parameter1,
    attachments,
    patientToBeSeen
  } = note;
  const { t } = useTranslation();
  const { caseDetails, userRole, assignerPrograms } = useEconsultCaseDetailsContext();
  const { selectedTab } = useEconsultTabContext();

  const isUserAssignerInProgram = (programId: number) => {
    const program = assignerPrograms?.assigner?.find((id: number) => id === programId);
    if (program) return true;
    return false;
  };
  const isCurrentUserCaseTriageAdmin = () => {
    return NoteTypes.triageAdmin === userRole;
  };

  const isCurrentUserCaseAssigner = () => {
    if (!isCurrentUserCaseTriageAdmin()) {
      return false;
    }
    return isUserAssignerInProgram(caseDetails?.participant?.program?.programId);
  };

  const isUserTriageAssignerInProgram = (programId: number | undefined) => {
    const program = assignerPrograms?.triageAssigner?.find((id: number) => id === programId);
    if (program) return true;
    return false;
  };

  const isCurrentUserCaseTriageAssigner = () => {
    if (!isCurrentUserCaseTriageAdmin()) {
      return false;
    }
    const programId = caseDetails?.participant?.program?.programId
      ? parseInt(caseDetails.participant.program.programId, 10)
      : undefined;
    return isUserTriageAssignerInProgram(programId);
  };

  const showNoteContent = () => {
    if (!userRole) return false;

    if (
      note &&
      note.actionTypeCd === NoteActionTypes.assign &&
      note.noteTypeCd === NoteTypes.triageAdmin &&
      note.noteContent
    ) {
      return true;
    }
    if (
      note &&
      note.noteContent &&
      (EconsultUserService.isCurrentUserCaseDepartmentalAssigner(
        userRole,
        assignerPrograms,
        caseDetails?.participant?.program?.programId
      ) ||
        isCurrentUserCaseAssigner()) &&
      (note.actionTypeCd === NoteActionTypes.decline ||
        note.actionTypeCd === NoteActionTypes.unassign) &&
      note.noteTypeCd === NoteTypes.consultant
    ) {
      return true;
    }
    if (isCurrentUserCaseTriageAdmin() && !isCurrentUserCaseTriageAssigner()) {
      return false;
    }
    if (
      userRole?.toUpperCase() === EconsultUserRoles.triage &&
      selectedTab === ConsultTab.Redirect
    ) {
      return false;
    }
    return true;
  };

  const getStatus = (status: string) => {
    switch (status) {
      case NoteActionTypes.send:
        return t('Case.notestatus.send');
      case NoteActionTypes.writeConsult:
        return t('Case.notestatus.writeConsult');
      case NoteActionTypes.askForMoreInfo:
        return t('Case.notestatus.askForMoreInfo');
      case NoteActionTypes.decline:
        return t('Case.notestatus.decline');
      case NoteActionTypes.finish:
        return t('Case.notestatus.finish');
      case NoteActionTypes.reply:
        return t('Case.notestatus.reply');
      case NoteActionTypes.cancel:
        return t('Case.notestatus.cancel');
      case NoteActionTypes.forward:
        return t('Case.notestatus.forward');
      case NoteActionTypes.addNote:
        return t('Case.notestatus.addNote');
      case NoteActionTypes.reopen:
        return t('Case.notestatus.reopen');
      case NoteActionTypes.recomplete:
        return t('Case.notestatus.recomplete');
      case NoteActionTypes.assign:
        return t('Case.notestatus.assign');
      case NoteActionTypes.unassign:
        return t('Case.notestatus.unassign');
      default:
        return t('Case.status.unknown');
    }
  };

  const getNoteHeading = () => {
    if (
      actionTypeCd === NoteActionTypes.assign &&
      noteContent &&
      noteContent.indexOf('automatically assigned') !== -1
    ) {
      return '';
    }
    if (NoteTypes.triageAdmin === noteTypeCd) {
      if (NoteActionTypes.forward === actionTypeCd) {
        return t('Case.note.cancelledDueToRedirection');
      }
      if (NoteActionTypes.addNote === actionTypeCd) {
        return `${note.lastUpdatedBy} ${t('Case.note.redirectCaseTo')} ${parameter1}`;
      }
    }

    let heading = `${note.lastUpdatedBy} ${getStatus(actionTypeCd || '')}`;
    if (parameter1) {
      if (noteTypeCd === NoteTypes.triageAdmin && actionTypeCd === NoteActionTypes.assign) {
        heading += ` to ${parameter1}`;
      } else if (
        (actionTypeCd === NoteActionTypes.decline || actionTypeCd === NoteActionTypes.unassign) &&
        parameter1
      ) {
        heading += `. Reason: ${parameter1}`;
      }
    }
    return heading;
  };
  return (
    <EconsultCaseNoteItemPaper>
      <Stack direction="row">
        <Stack flexGrow={1}>
          <Typography variant="body2" color="base.grey4">
            {getNoteHeading()}
          </Typography>
        </Stack>
        <Typography variant="body2" color="base.grey4">
          {moment(lastUpdated).format('MMM DD, yyyy  h:mm A')}
        </Typography>
      </Stack>
      {noteContent && showNoteContent() && (
        <Typography variant="body2" color="base.grey3" mt={1}>
          <>
            {noteContent.split('\n').map((item, key) => {
              return (
                <span key={key}>
                  {item}
                  <br />
                </span>
              );
            })}
          </>
        </Typography>
      )}
      {patientToBeSeen && (
        <Stack direction="row" mt={1} columnGap={0.5}>
          <CheckMarkIcon />
          <Typography variant="body4">{t('Case.consult.recommendation')}</Typography>
        </Stack>
      )}
      {showNoteContent() &&
        attachments &&
        attachments
          .sort((a: any, b: any) => {
            if (a.uploadedFile?.createdOn > b.uploadedFile?.createdOn) return 1;
            if (a.uploadedFile?.createdOn < b.uploadedFile?.createdOn) return -1;
            return 0;
          })
          .filter((f) => f.statusCd !== 'PENDING')
          .map((a) => <EconsultAttachmentLink key={a.attachmentId} attachment={a} />)}
    </EconsultCaseNoteItemPaper>
  );
};

export default EconsultCaseNoteItem;
